import { formulationDisplay, MEDICATION_CODE_SYSTEMS } from '@liza/fhir-utils';
import type { ValueSetCode } from '../ValueSetCombobox';
import { freeTextName, isFreeTextItem } from './drugSearchSelection';
import type { PrescribableMedication } from './types';

// Re-exported so the picker's public surface is unchanged; the rule itself lives in `@liza/fhir-utils`,
// where the read path that renders a recorded formulation shares it.
export { formulationDisplay };

/** A medication input field — an RxNorm-coded product or text-only (free-text/manual entry). This
 * is the shape the create mutations accept (`carePlanTopic.createMedication` `medication`,
 * `medicationRequest.createFromInput` `input.medication`). */
export type MedicationInput = { coding: [{ system: string; code: string; display?: string }] } | { text: string };

/**
 * The product `ValueSetCode` for a chosen formulation. ScriptSure formulation labels can be
 * strength/form only — carry the drug name in. RxNorm-catalog fallback names are already complete
 * product names (drug + strength + form), so use them as-is: composing would double the drug name.
 *
 * Codes RxNorm where there is one, else NDC — devices and supplies are NDC-coded only, and
 * `$order-medication` accepts either. Returns null for a formulation carrying neither, which the
 * picker treats as manual entry.
 */
export function productFromFormulation(
  drugName: string | undefined,
  formulation: PrescribableMedication,
): ValueSetCode | null {
  const code = formulation.rxNorm ?? formulation.ndc;
  if (!code) return null;
  return {
    system: formulation.rxNorm ? MEDICATION_CODE_SYSTEMS.RXNORM : MEDICATION_CODE_SYSTEMS.NDC,
    code,
    display: formulation.catalog === 'rxnorm' ? formulation.name : formulationDisplay(drugName, formulation.name),
  };
}

/**
 * Build the `medication` CodeableConcept for a create/update mutation from the form's product.
 *
 * For a coded formulation we send the RxNorm coding with its composed display — but NO `text`. The
 * server resolves a consumer-friendly SCDC/SBDC label from the RxNorm code at write time
 * (`resolveMedicationDisplay`), which short-circuits on any supplied `text`: sending ScriptSure's
 * name (e.g. "ER 500 mg tablet,extended release 24 hr") as text would pre-empt that and land the
 * wrong display on the plan + its task. The coding `display` doesn't pre-empt anything — it's the
 * fallback when resolution fails: ScriptSure's catalog can hand out a retired RxNorm code the
 * terminology no longer carries, and without a display the plan's name degrades to the bare code
 * (e.g. "1009464").
 *
 * A free-text sentinel product (see {@link isFreeTextItem}) has no code, so its typed name is all
 * we have — the server stores it verbatim, uncoded (not prescribable, won't sync to ScriptSure).
 */
export function medicationInputFromProduct(product: ValueSetCode): MedicationInput {
  if (isFreeTextItem(product)) return { text: freeTextName(product) };
  return {
    coding: [
      {
        system: product.system ?? MEDICATION_CODE_SYSTEMS.RXNORM,
        code: product.code,
        ...(product.display ? { display: product.display } : {}),
      },
    ],
  };
}
